/**
 * 数据集文件相关API服务
 * 包含数据集文件的增删查改、解析、预览等功能
 */

import { fetcher } from '../base/fetcher'
import type {
  DatasetFileListResponse,
  GetDatasetFileListRequest,
  ParseDatasetFileRequest,
  SublevelPreviewRequest,
  SublevelPreviewResponse,
  UploadDatasetFileRequest,
  SaveDatasetConfigRequest,
  GetTableSettingRequest,
  ExcelParseResult,
  DbDataTypeEnum,
  DocumentProcessingConfig,
} from '../types/datasetFiles.type'

/**
 * 数据集文件相关API路径常量
 */
export const DATASET_FILES_API_PATHS = {
  /** 获取数据集文件列表 */
  LIST: '/knowledgeBase/datasetFile/list',
  /** 删除数据集文件 */
  DELETE: '/knowledgeBase/datasetFile/delById',
  /** 解析文件 */
  PARSE: '/knowledgeBase/datasetFile/parse',
  /** 分段预览 */
  SUBLEVEL_PREVIEW: '/knowledgeBase/datasetFile/sublevelPreview',
  /** 上传文件 */
  UPLOAD: '/knowledgeBase/datasetFile/upload',
  /** 保存数据集配置 */
  SAVE_CONFIG: '/knowledgeBase/datasetConfig/save',
  /** 获取数据库数据类型枚举 */
  LIST_DB_DATA_TYPE_ENUM: '/knowledgeBase/datasetFile/table/listDbDataTypeEnum',
  /** 获取表格设置 */
  GET_TABLE_SETTING: '/knowledgeBase/datasetFile/table/getSetting',
  /** 保存表格设置并解析 */
  SAVE_TABLE_SETTING_AND_PARSE:
    '/knowledgeBase/datasetFile/table/saveSettingAndPase',
  /** 保存表格设置 */
  SAVE_TABLE_SETTING: '/knowledgeBase/datasetFile/table/saveOrUpdateSetting',
  /** 解析表格 */
  PARSE_TABLE: '/knowledgeBase/datasetFile/table/parse',
  /** 获取表格分页数据 */
  GET_TABLE_PAGE_RECORDS: '/knowledgeBase/datasetFile/table/pageRecords',
  /** 根据文件ID获取数据集配置 */
  GET_CONFIG_BY_FILE_ID: '/knowledgeBase/datasetConfig/getConfigByFileId',
} as const

/**
 * TableColumnsSimpleReq，表格列信息表
 */
export interface TableColumnsSimpleReq {
  /**
   * 表格的列索引
   */
  cellIndex?: number
  /**
   * 列描述
   */
  columnDesc?: string
  /**
   * 列名
   */
  columnName?: string
  /**
   * 列类型
   */
  columnType?: string
  /**
   * 是否是索引
   */
  isIndex?: number
  [property: string]: any
}

/**
 * ParseSettingReq
 */
export interface SaveTableSettingRequest {
  /**
   * 数据集文件id
   */
  datasetFileId?: number
  /**
   * 数据集id
   */
  datasetId?: number
  /**
   * 数据起始行
   */
  dataStartRows?: number
  /**
   * 表头行数
   */
  headerRows?: number
  /**
   * 知识库id
   */
  knowledgeBaseId?: number
  /**
   * 字段列表
   */
  tableColumnsList?: TableColumnsSimpleReq[]
  /**
   * 数据表名
   */
  tableName?: string
  /**
   * 工作空间id
   */
  workspaceId?: number
  [property: string]: any
}

/**
 * ParseTableRequest
 */
export interface ParseTableRequest {
  /**
   * 数据集文件id
   */
  datasetFileId?: number
  /**
   * 数据集id
   */
  datasetId?: number
  /**
   * 知识库id
   */
  knowledgeBaseId?: number
  /**
   * 工作空间id
   */
  workspaceId?: number
  [property: string]: any
}

/**
 * PageRecordReq，分页获取列表参数
 */
export interface GetTablePageRecordsRequest {
  /**
   * 数据集文件id
   */
  datasetFileId?: number
  /**
   * 数据集id
   */
  datasetId?: number
  /**
   * 知识库id
   */
  knowledgeBaseId?: number
  /**
   * 页码，从 1 开始
   */
  pageNo: number
  /**
   * 每页条数，最大值为 100
   */
  pageSize: number
  /**
   * 工作空间id
   */
  workspaceId?: number
  [property: string]: any
}

export interface TableColumn {
  id: string
  cellIndex: number
  columnDesc: string
  columnName: string
  columnNo: number
  columnType: string
  createTime: number
  creator: string
  datasetFileId: string
  dbColumnName: string
  isIndex: number
  knowledgeBaseId: string
  tableMetadataId: string
  updateTime: number
  updater: string
  workspaceId: string
}

/**
 * PageRes«Map«string,object»»
 */
export interface TablePageRecordsResponse {
  /**
   * 当前页
   */
  current?: string
  /**
   * 存放查询出来的数据
   */
  records?: Record<string, any>[]
  /**
   * 每页显示条数
   */
  size?: string
  /**
   * 返回记录的总数
   */
  total?: string
  /**
   * 表格列定义
   */
  tableColumns?: TableColumn[]
  [property: string]: any
}

/**
 * 根据文件ID获取数据集配置响应
 */
export interface GetConfigByFileIdResponse {
  analyticalStrategies: number
  configDetail: DocumentProcessingConfig
  id: number
  partitioningStrategies: number
}

/**
 * 数据集文件服务对象
 * 提供所有数据集文件相关的API调用方法
 */
export const datasetFilesService = {
  /**
   * 获取数据集文件列表
   * @param params 查询参数（数据集ID、搜索关键词、状态等）
   * @returns 数据集文件列表响应
   */
  getDatasetFileList: (
    params: GetDatasetFileListRequest
  ): Promise<DatasetFileListResponse> => {
    // 将参数转换为字符串类型以符合fetcher要求
    const stringParams: Record<string, string> = {}
    if (params.datasetId) stringParams.datasetId = params.datasetId
    if (params.search) stringParams.search = params.search
    if (params.status !== undefined)
      stringParams.status = params.status.toString()
    if (params.parseStatus !== undefined)
      stringParams.parseStatus = params.parseStatus.toString()

    return fetcher.get<DatasetFileListResponse>(DATASET_FILES_API_PATHS.LIST, {
      params: stringParams,
    })
  },

  /**
   * 删除数据集文件
   * @param id 文件ID
   * @returns 删除操作结果
   */
  deleteDatasetFile: (id: string): Promise<boolean> =>
    fetcher.post<boolean>(DATASET_FILES_API_PATHS.DELETE, {
      id,
    }),

  /**
   * 解析文件
   * @param params 解析参数
   * @returns 解析操作结果
   */
  parseDatasetFile: (params: ParseDatasetFileRequest): Promise<boolean> =>
    fetcher.post<boolean>(DATASET_FILES_API_PATHS.PARSE, params),

  /**
   * 分段预览
   * @param params 预览参数
   * @returns 分段预览响应
   */
  getSublevelPreview: (
    params: SublevelPreviewRequest
  ): Promise<SublevelPreviewResponse> => {
    // 将参数转换为字符串类型以符合fetcher要求
    const stringParams: Record<string, string> = {}
    if (params.datasetFileId) stringParams.datasetFileId = params.datasetFileId
    if (params.datasetId) stringParams.datasetId = params.datasetId
    if (params.knowledgeBaseId)
      stringParams.knowledgeBaseId = params.knowledgeBaseId
    if (params.workspaceId) stringParams.workspaceId = params.workspaceId

    return fetcher.get<SublevelPreviewResponse>(
      DATASET_FILES_API_PATHS.SUBLEVEL_PREVIEW,
      {
        params: stringParams,
      }
    )
  },

  /**
   * 上传文件
   * @param params 上传参数
   * @returns 上传操作结果，返回文件ID列表
   */
  uploadDatasetFile: (params: UploadDatasetFileRequest): Promise<string[]> => {
    const formData = new FormData()
    formData.append('datasetId', params.datasetId)
    formData.append('knowledgeBaseId', params.knowledgeBaseId)
    formData.append('workspaceId', params.workspaceId)

    // 如果是单个文件，转换为数组
    const files = Array.isArray(params.file) ? params.file : [params.file]

    // 添加所有文件到 files 字段
    files.forEach((file) => {
      formData.append('files', file)
    })

    // 不要设置任何 headers，让 fetcher 处理
    return fetcher.post<string[]>(DATASET_FILES_API_PATHS.UPLOAD, formData)
  },

  /**
   * 根据文件ID获取数据集配置
   * @param fileId 文件ID
   * @returns 数据集配置信息
   */
  getConfigByFileId: async (
    fileId: string
  ): Promise<GetConfigByFileIdResponse> => {
    return await fetcher.get<GetConfigByFileIdResponse>(
      DATASET_FILES_API_PATHS.GET_CONFIG_BY_FILE_ID,
      {
        params: { fileId },
      }
    )
  },

  /**
   * 保存数据集配置
   * @param params 配置参数
   * @returns 保存操作结果
   */
  saveDatasetConfig: (params: SaveDatasetConfigRequest): Promise<boolean> =>
    fetcher.post<boolean>(DATASET_FILES_API_PATHS.SAVE_CONFIG, params),

  /**
   * 获取数据库数据类型枚举
   * @returns 数据库数据类型枚举
   */
  getDbDataTypeEnum: (): Promise<DbDataTypeEnum[]> =>
    fetcher.get<DbDataTypeEnum[]>(
      DATASET_FILES_API_PATHS.LIST_DB_DATA_TYPE_ENUM
    ),

  /**
   * 获取表格设置
   * @param params 请求参数（数据集ID、文件ID等）
   * @returns 表格设置信息
   */
  getTableSetting: (
    params: GetTableSettingRequest
  ): Promise<ExcelParseResult> =>
    fetcher.post<ExcelParseResult>(
      DATASET_FILES_API_PATHS.GET_TABLE_SETTING,
      params
    ),

  /**
   * 保存表格设置并解析
   * @param params 表格设置参数
   * @returns 保存和解析操作结果
   */
  saveTableSettingAndParse: async (
    params: SaveTableSettingRequest
  ): Promise<boolean> => {
    return await fetcher.post<boolean>(
      DATASET_FILES_API_PATHS.SAVE_TABLE_SETTING_AND_PARSE,
      params
    )
  },

  /**
   * 保存表格设置
   * @param params 表格设置参数
   * @returns 保存操作结果
   */
  saveTableSetting: async (
    params: SaveTableSettingRequest
  ): Promise<boolean> => {
    return await fetcher.post<boolean>(
      DATASET_FILES_API_PATHS.SAVE_TABLE_SETTING,
      params
    )
  },

  /**
   * 解析表格
   * @param params 解析参数
   * @returns 解析操作结果
   */
  parseTable: async (params: ParseTableRequest): Promise<boolean> => {
    return await fetcher.post<boolean>(
      DATASET_FILES_API_PATHS.PARSE_TABLE,
      params
    )
  },

  /**
   * 获取表格分页数据
   * @param params 分页参数
   * @returns 分页数据
   */
  getTablePageRecords: async (
    params: GetTablePageRecordsRequest
  ): Promise<TablePageRecordsResponse> => {
    return await fetcher.post<{
      code?: number
      data?: TablePageRecordsResponse
      msg?: string
    }>(DATASET_FILES_API_PATHS.GET_TABLE_PAGE_RECORDS, params)
  },
}
